import * as React from 'react';
import { getBaseUrl } from '@/redux/modules/config';
import {
    getCatalogAddress,
    getCatalogCoverLots,
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveBiddingUrl, getMediumItemImageUrl } from '@/utils/urls';
import { safelyEscapeText } from '@/utils/safelyRender';
import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';

type Props = {
    catalogId: number;
};

const BiddingConsolePageHeaderMeta = ({ catalogId }: Props) => {
    const { formatMessage } = useIntl();

    const baseUrl = useAppSelector(getBaseUrl);
    const address = useAppSelector((state) => getCatalogAddress(state, catalogId));
    const coverLots = useAppSelector((state) => getCatalogCoverLots(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const name = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));

    const [{ imageVersion, itemId }] = Boolean(coverLots.length) ? coverLots : [{ imageVersion: 0, itemId: 0 }];
    const { city, fullCountryName: country, state: stateCode } = address;

    const location = [city, stateCode, country].filter((element) => element).join(', ');

    const description =
        `${formatMessage({ id: 'auctioneer' })}: ${name}, ` +
        `${catalogTitle}, ` +
        `${formatMessage({ id: 'date' })}: ${shortFormatDate(saleStartTs)}, ` +
        `${formatMessage({ id: 'location' })}: ${location}`;

    const imageUrl = getMediumItemImageUrl(sellerId, catalogId, itemId, 1, imageVersion);
    const safeDescription = safelyEscapeText(description);

    const title = useAddNotificationCountToTitle(safelyEscapeText(`${name} - ${catalogTitle}`));
    const consolePath = getLiveBiddingUrl(catalogId);
    const canonical = `${baseUrl}${consolePath}`;

    const style = [
        {
            cssText: `
                body {
                    height: 100%;
                }
                #content > .form-link-container > .form-link {
                    left: 30px;
                    bottom: -5px;
                    transform: none;
                }
            `,
        },
    ];

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={safeDescription}
            imageUrl={imageUrl}
            style={style}
            title={title}
        />
    );
};

export default React.memo<Props>(BiddingConsolePageHeaderMeta);
